import { useEffect, useState } from 'react';
import { listFolders, listProjectIndex } from './projectApi';
import { pathOf } from './folderTree';

/*
 * A tiny, shared, lazily-loaded index of the signed-in account's library — folders + { id, name, folder_id } of every
 * live publication. It is what the document tabs use to colour-code open publications by folder without fetching the
 * whole library (pages included) for every tab. One request per page load, re-fetched whenever the library changes
 * (`wp:library-changed` — moves, deletes, restores, imports); every mount forces a refresh, and signing out clears it (AuthContext).
 */

let cache = { folders: [], projects: [], loaded: false };
let inflight = null;
const listeners = new Set();

const publish = () => listeners.forEach((fn) => fn(cache));

export const loadLibraryIndex = async (force = false) => {
  if (inflight) return inflight;
  if (cache.loaded && !force) return cache;
  inflight = (async () => {
    try {
      const [folders, projects] = await Promise.all([listFolders(), listProjectIndex()]);
      cache = { folders, projects, loaded: true };
    } catch {
      cache = { ...cache, loaded: true }; // signed out / offline: keep whatever we had, do not hammer the API
    } finally {
      inflight = null;
    }
    publish();
    return cache;
  })();
  return inflight;
};

export const resetLibraryIndex = () => { cache = { folders: [], projects: [], loaded: false }; publish(); };

/** { folders, projects, loaded } — subscribes to updates; the first subscriber triggers the load. */
export const useLibraryIndex = () => {
  const [idx, setIdx] = useState(cache);
  useEffect(() => {
    listeners.add(setIdx);
    loadLibraryIndex(true); // always fresh on mount — the previous account's colours must never bleed into this one
    const refresh = () => loadLibraryIndex(true);
    window.addEventListener('wp:library-changed', refresh);
    return () => { listeners.delete(setIdx); window.removeEventListener('wp:library-changed', refresh); };
  }, []);
  return idx;
};

/** The live folder a publication sits in (with its full path for tooltips), or null when unfiled / unknown. */
export const folderOfProject = (idx, projectId) => {
  if (!projectId || !idx?.projects?.length) return null;
  const p = idx.projects.find((x) => x.id === projectId);
  if (!p?.folder_id) return null;
  const f = idx.folders.find((x) => x.id === p.folder_id);
  return f ? { ...f, path: pathOf(idx.folders, f.id) } : null;
};
